import { useCallback, useEffect, useMemo, useState } from 'react';

import type { UseWizardReturn, WizardConfig } from '@/components/wizards/types';

export function useWizard<TFormData>(
  config: WizardConfig<TFormData>
): UseWizardReturn<TFormData> {
  const { steps, initialData, onSubmit } = config;

  // Validate steps on mount
  if (!steps || steps.length === 0) {
    throw new Error('useWizard: steps array cannot be empty');
  }

  // State
  const [formData, setFormData] = useState<TFormData>(initialData);
  const [currentStepId, setCurrentStepId] = useState<string>(steps[0].id);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Get visible steps (not skipped based on current formData)
  const visibleSteps = useMemo(() => {
    return steps
      .filter(step => !step.shouldSkip?.(formData))
      .sort((a, b) => a.order - b.order);
  }, [steps, formData]);

  // Warn if all steps are skipped
  useEffect(() => {
    if (visibleSteps.length === 0) {
      console.warn('useWizard: All steps are being skipped');
    }
  }, [visibleSteps.length]);

  // Current step config
  const currentStepConfig = useMemo(() => {
    return steps.find(s => s.id === currentStepId) || steps[0];
  }, [steps, currentStepId]);

  // Auto-navigate if current step becomes skipped
  useEffect(() => {
    const isCurrentStepSkipped = currentStepConfig.shouldSkip?.(formData);
    if (isCurrentStepSkipped && visibleSteps.length > 0) {
      const currentIndex = visibleSteps.findIndex(s => s.id === currentStepId);
      if (currentIndex === -1) {
        // Current step not in visible, go to first visible
        setCurrentStepId(visibleSteps[0].id);
      }
    }
  }, [formData, currentStepConfig, currentStepId, visibleSteps]);

  // Navigation computed values
  const currentVisibleIndex = visibleSteps.findIndex(
    s => s.id === currentStepId
  );
  const isFirstStep = currentVisibleIndex === 0;
  const isLastStep = currentVisibleIndex === visibleSteps.length - 1;

  // Validation
  const validateStep = useCallback(() => {
    const step = currentStepConfig;
    if (step.validate) {
      const isValid = step.validate(formData);
      if (!isValid) {
        setTouched(prev => ({ ...prev, [step.id]: true }));
      }
      return isValid;
    }
    return true;
  }, [currentStepConfig, formData]);

  const validateForm = useCallback(() => {
    return visibleSteps.every(step => {
      if (step.validate) {
        return step.validate(formData);
      }
      return true;
    });
  }, [visibleSteps, formData]);

  const isCurrentStepValid = useMemo(() => {
    if (currentStepConfig.validate) {
      return currentStepConfig.validate(formData);
    }
    return true;
  }, [currentStepConfig, formData]);

  // Navigation handlers
  const handleNext = useCallback(() => {
    if (!isCurrentStepValid) return;

    setTouched(prev => ({ ...prev, [currentStepId]: true }));

    if (isLastStep) return;

    const nextIndex = currentVisibleIndex + 1;
    if (nextIndex < visibleSteps.length) {
      setCurrentStepId(visibleSteps[nextIndex].id);
    }
  }, [
    isCurrentStepValid,
    isLastStep,
    currentVisibleIndex,
    visibleSteps,
    currentStepId,
  ]);

  const handleBack = useCallback(() => {
    if (isFirstStep) return;

    const prevIndex = currentVisibleIndex - 1;
    if (prevIndex >= 0) {
      setCurrentStepId(visibleSteps[prevIndex].id);
    }
  }, [isFirstStep, currentVisibleIndex, visibleSteps]);

  const goToStep = useCallback(
    (stepId: string) => {
      const targetStep = visibleSteps.find(s => s.id === stepId);
      if (targetStep) {
        setCurrentStepId(stepId);
      }
    },
    [visibleSteps]
  );

  // Progress
  // Percentage is validation-aware: only count current step if it's valid
  // - Invalid step: previous steps only (currentVisibleIndex / total)
  // - Valid step: previous steps + current (currentVisibleIndex + 1 / total)
  // This ensures 100% only shows when the last step is actually complete.
  const progress = useMemo(
    () => ({
      currentDisplayStep: currentVisibleIndex + 1,
      totalDisplaySteps: visibleSteps.length,
      percentage:
        visibleSteps.length > 0
          ? Math.round(
              ((isCurrentStepValid
                ? currentVisibleIndex + 1 // Previous steps + current (valid)
                : currentVisibleIndex) / // Only previous steps (current incomplete)
                visibleSteps.length) *
                100
            )
          : 0,
      currentStepTitle: currentStepConfig.title,
    }),
    [
      currentVisibleIndex,
      visibleSteps.length,
      currentStepConfig.title,
      isCurrentStepValid,
    ]
  );

  // Submit
  const submit = useCallback(async () => {
    if (!validateForm()) {
      setSubmitError('Please complete all required fields');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      await onSubmit?.(formData);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Submission failed');
    } finally {
      setIsSubmitting(false);
    }
  }, [validateForm, onSubmit, formData]);

  // Reset
  const reset = useCallback(() => {
    setFormData(initialData);
    setCurrentStepId(steps[0].id);
    setIsSubmitting(false);
    setSubmitError(null);
    setErrors({});
    setTouched({});
  }, [initialData, steps]);

  const clearSubmitError = useCallback(() => {
    setSubmitError(null);
  }, []);

  return {
    currentStepId,
    currentStepConfig,
    formData,
    setFormData,
    navigation: {
      handleNext,
      handleBack,
      goToStep,
      canGoNext: isCurrentStepValid,
      canGoBack: !isFirstStep,
      isLastStep,
      isFirstStep,
    },
    progress,
    validation: {
      isCurrentStepValid,
      errors,
      touched,
      validateStep,
      validateForm,
    },
    submit,
    reset,
    isSubmitting,
    submitError,
    clearSubmitError,
  };
}
